import React from 'react';

const Section9VendorMatching = () => (
  <section className="group bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border-2 border-cyan-200/50 mb-8 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-500">
    <h2 className="text-3xl font-serif font-bold mb-2 flex items-center transition-transform duration-300 origin-left group-hover:scale-105">
      <span className="text-3xl mr-3">🏪</span>
      <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
        Smart Vendor Matching
      </span>
    </h2>
    <p className="text-base text-gray-500 font-light italic ml-12 mb-8">AI solving a real matching problem, not keyword filters</p>

    {/* Stats bar */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
      {[
        { icon: '🏪', value: '250+', label: 'Vendors Profiled' },
        { icon: '📐', value: '6', label: 'Vector Dimensions' },
        { icon: '⚡', value: '50ms', label: 'Match Latency' },
        { icon: '📂', value: '8', label: 'Vendor Categories' },
      ].map((stat, i) => (
        <div key={i} className="bg-gradient-to-br from-cyan-50 to-blue-50/50 rounded-2xl p-4 text-center border border-cyan-200/50 hover:scale-105 transition-transform duration-300">
          <span className="text-2xl block mb-1">{stat.icon}</span>
          <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
          <p className="text-sm text-gray-500 font-medium">{stat.label}</p>
        </div>
      ))}
    </div>

    {/* Two-phase architecture */}
    <div className="bg-gradient-to-br from-cyan-50/50 to-blue-50/50 rounded-2xl p-6 border border-cyan-200/30 mb-6">
      <h3 className="text-base font-bold text-cyan-600 uppercase tracking-wider mb-5 text-center">Two-Phase Architecture: One-Time Setup, Real-Time Math</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Phase 1: Onboarding */}
        <div className="bg-white/80 rounded-xl p-5 border border-cyan-200/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-800 text-sm">Phase 1: Vendor Profiling</h4>
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-500 text-white">One-Time</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">Two-step process: GPT-4o-mini generates authentic vendor taglines and descriptions, then SQL pattern matching derives 6D vector profiles from that content.</p>
          <div className="bg-cyan-50 rounded-lg px-3 py-2">
            <p className="text-xs font-mono text-cyan-700">Vendor → AI generates content → SQL derives 6D vectors → Database</p>
          </div>
          <p className="text-xs text-gray-400 mt-2">AI cost: ~$0.001 per vendor (content generation only) · Vectors: pure SQL</p>
        </div>

        {/* Phase 2: Matching */}
        <div className="bg-white/80 rounded-xl p-5 border border-blue-200/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-gray-800 text-sm">Phase 2: Theme Matching</h4>
            <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white">Real-Time</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">Weighted cosine similarity across 6 dimensions. Cultural vector gets special treatment: exact culture matches are prioritized over general similarity.</p>
          <div className="bg-blue-50 rounded-lg px-3 py-2">
            <p className="text-xs font-mono text-blue-700">Theme 6D Vector × Vendor 6D Vector → Weighted Score → Ranked</p>
          </div>
          <p className="text-xs text-gray-400 mt-2">Cost: $0 (pure math) · Zero AI API calls at runtime</p>
        </div>
      </div>
    </div>

    {/* 6-Dimensional vector profile */}
    <div className="bg-gradient-to-br from-cyan-50/50 to-blue-50/50 rounded-2xl p-6 border border-cyan-200/30 mb-6">
      <h3 className="text-base font-bold text-cyan-600 uppercase tracking-wider mb-5 text-center">6-Dimensional Vendor AI Profile</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { dim: 'Cultural Vector', example: 'indian: 80, western: 95, jewish: 60', icon: '🌍', desc: 'Cultural expertise', weight: '35%' },
          { dim: 'Style Vector', example: 'romantic: 95, elegant: 85, rustic: 20', icon: '🎨', desc: 'Aesthetic specialization', weight: '25%' },
          { dim: 'Mood Vector', example: 'intimate: 85, sophisticated: 75, playful: 40', icon: '💫', desc: 'Emotional atmosphere', weight: '15%' },
          { dim: 'Color Vector', example: 'pastels: 90, warm_tones: 80, bold: 30', icon: '🌈', desc: 'Color palette affinity', weight: '15%' },
          { dim: 'Formality Vector', example: 'formal: 70, semi_formal: 90, casual: 30', icon: '👔', desc: 'Event formality level', weight: '5%' },
          { dim: 'Seasonal Vector', example: 'spring: 85, summer: 90, fall: 70, winter: 50', icon: '🍂', desc: 'Seasonal strength', weight: '5%' },
        ].map((d, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-cyan-100/50 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <span className="text-lg mr-2">{d.icon}</span>
                <h4 className="text-sm font-bold text-gray-800">{d.dim}</h4>
              </div>
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-500 text-white">{d.weight}</span>
            </div>
            <p className="text-xs text-gray-500 mb-2">{d.desc}</p>
            <div className="bg-cyan-50/50 rounded-lg px-2 py-1">
              <p className="text-[10px] font-mono text-cyan-700 break-all">{d.example}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Search intelligence */}
    <div className="bg-gradient-to-r from-cyan-50/80 to-blue-50/80 rounded-2xl p-5 border border-cyan-200/30">
      <h3 className="text-base font-bold text-cyan-600 uppercase tracking-wider mb-3">🎯 Search Intelligence</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { q: 'Cultural vector gets special treatment', a: 'Unlike other dimensions that use cosine similarity, cultural matching prioritizes exact culture matches. A vendor scoring 80+ in "Indian" gets a 95 compatibility, not averaged down by other cultures.' },
          { q: 'Location abbreviation handling', a: 'Short terms (LA, SF, NYC) use word boundary matching to prevent "LA" matching "Portland" or "Philadelphia". Full city names use flexible text matching.' },
          { q: 'Budget-tier mapping', a: 'Natural language → price symbols: "affordable" → $/$$ , "luxury" → $$$/$$$$ . "Any budget" or "flexible" omits the filter entirely.' },
          { q: 'Category search', a: '8 categories: Venue, Catering, Photography, Flowers, Music, Attire, Transportation, Planning. ILIKE matching for flexibility.' },
          { q: 'Result ranking', a: 'Vendors sorted by rating DESC within category. Theme matching adds compatibility score overlay. VowBot limits to top 5 per search.' },
        ].map((item, i) => (
          <div key={i} className="bg-white/80 rounded-xl p-4 border border-cyan-100/50">
            <p className="text-sm font-bold text-cyan-700 mb-1">{item.q}</p>
            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Section9VendorMatching;
